import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import useSuggestions from '../hooks/use-suggestions.js';
import {useDebounce} from '../hooks/use-debounce.js';
import {useSearchHistory} from '../hooks/use-search-history.js';
import {KEYBOARD_FOCUS_RING_CLASSES, useKeyboardFocus} from '../hooks/use-keyboard-focus.js';
import {cleanSearchValue, isValidSearchValue} from '../../helper/search-helper.js';
import {SEARCH_INPUT_DELAY, SEARCH_QUERY_URL_PARAM, SEARCH_ORIGIN_QUERY_URL_PARAM} from '../navigation/constants.js';
import {SearchDropdownLegacy} from './SearchDropdownLegacy.jsx';
import {useBreakpointBetween, Icon} from 'bonprix-design-system';
import useSearchTexts from '@header/scripts/components/hooks/use-search-texts.js';
import {HeaderContext} from '@header/scripts/components/context/HeaderContext.jsx';

const SEARCH_ORIGIN_SEARCH = 'textfield';
const SEARCH_ORIGIN_SUGGESTION = 'suggest';
const SEARCH_ORIGIN_HISTORY = 'lastsearch';

const SUGGESTIONS_LIST_MODE = 'SUGGESTIONS';
const HISTORY_LIST_MODE = 'HISTORY';

const SearchFieldLegacy = ({searchUrl, noResultsFound, hideLogo, showLogo}) => {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(
        params.get(SEARCH_ORIGIN_QUERY_URL_PARAM) || params.get(SEARCH_QUERY_URL_PARAM) || ''
    );
    const [isDropdownExpanded, setIsDropdownExpanded] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [selectionMode, setSelectionMode] = useState('HOVER');

    const deferredSearchTerm = useDebounce(searchTerm, SEARCH_INPUT_DELAY);
    const {recentSearches, addToSearchHistory, clearSearchHistory} = useSearchHistory();
    const isMobile = useBreakpointBetween('s', 'xl');
    const {data: searchTextsData} = useSearchTexts();
    const {data: suggestionsData} = useSuggestions(deferredSearchTerm);
    const {headerLightMode} = useContext(HeaderContext);
    const keyboardFocus = useKeyboardFocus();

    const searchContainerRef = useRef(null);
    const inputRef = useRef(null);

    const listMode = useMemo(() => (searchTerm.trim() ? SUGGESTIONS_LIST_MODE : HISTORY_LIST_MODE), [searchTerm]);



    useEffect(() => {
        if (noResultsFound && isMobile) {
            setIsOpen(true);
            hideLogo();
        } else {
            setIsOpen(false);
            showLogo();
        }
    }, [isMobile]);

    useEffect(() => {
        if (isOpen && isMobile && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen, isMobile]);

    const currentList = useMemo(() => {
        return listMode === HISTORY_LIST_MODE
            ? recentSearches
            : Array.isArray(suggestionsData?.termSuggestions)
                ? suggestionsData.termSuggestions.filter((suggestion) => typeof suggestion?.value === 'string')
                    .map((suggestion) => suggestion.value)
                : [];
    }, [listMode, recentSearches, suggestionsData]);

    const handleSearchChange = (term) => {
        setSearchTerm(term);
        setSelectedIndex(-1);
    };

    const handleFocus = () => {
        setIsDropdownExpanded(true);
    };

    const handleBlur = () => {
        setTimeout(() => {
            const isSearchContainerFocussed = searchContainerRef.current?.contains(document.activeElement);
            if (!isSearchContainerFocussed) {
                setIsDropdownExpanded(false);
                setSelectedIndex(-1);
                if (isMobile) {
                    setIsOpen(false);
                    showLogo();
                }
            }
        }, 0);
    };

    const handleKeyDown = (e) => {
        const listVisible = isDropdownExpanded && currentList.length > 0;

        const navigateNext = () => {
            if (!listVisible) {
                setIsDropdownExpanded(true);
                setSelectedIndex(0);
            } else {
                setSelectedIndex((prevIndex) => (prevIndex + 1) % currentList.length);
            }
        };

        const navigatePrevious = () => {
            if (listVisible) {
                setSelectedIndex((prevIndex) => (prevIndex - 1 + currentList.length) % currentList.length);
            }
        };

        const selectItem = () => {
            if (selectedIndex >= 0 && listVisible) {
                e.preventDefault();
                const selectedItem = currentList[selectedIndex];
                handleSelectItem(selectedItem);
            } else {
                performSearch(searchTerm, SEARCH_ORIGIN_SEARCH);
            }
        };

        const closeList = () => {
            setIsDropdownExpanded(false);
            setSelectedIndex(-1);
        };

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectionMode('SELECT');
                navigateNext();
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectionMode('SELECT');
                navigatePrevious();
                break;
            case 'Enter':
                selectItem();
                break;
            case 'Escape':
                closeList();
                break;
            case 'Tab':
                if (e.shiftKey) {
                    if (selectedIndex <= 0 || !listVisible) {
                        closeList();
                        return;
                    }
                    e.preventDefault();
                    setSelectionMode('SELECT');
                    navigatePrevious();
                    return;
                } else {
                    if (selectedIndex === currentList.length - 1) {
                        closeList();
                        return;
                    }
                    e.preventDefault();
                    setSelectionMode('SELECT');
                    navigateNext();
                }
                break;
            default:
                break;
        }
    };

    const handleSelectItem = (value) => {
        setSearchTerm(value);
        let origin = SEARCH_ORIGIN_SEARCH;

        if (listMode === SUGGESTIONS_LIST_MODE) {
            origin = SEARCH_ORIGIN_SUGGESTION;
        } else if (listMode === HISTORY_LIST_MODE) {
            origin = SEARCH_ORIGIN_HISTORY;
        }

        setIsDropdownExpanded(false);
        setSelectedIndex(-1);
        performSearch(value, origin);
    };

    function handleSearchResultItemHover(index) {
        setSelectedIndex(index);
        setSelectionMode('HOVER');
    }

    function unselectField() {
        setSelectedIndex(-1);
    }

    const handleButtonClick = (e) => {
        if (isMobile) {
            e.preventDefault();
            if (isOpen) {
                performSearch(searchTerm, SEARCH_ORIGIN_SEARCH);
            } else {
                setIsOpen(true);
                hideLogo();
            }
        } else {
            performSearch(searchTerm, SEARCH_ORIGIN_SEARCH);
        }
    };

    const performSearch = (passedSearchTerm, searchOrigin) => {
        const cleanedSearchValue = cleanSearchValue(passedSearchTerm);
        if (cleanedSearchValue.length === 0 || !isValidSearchValue(cleanedSearchValue)) {
            return;
        }

        inputRef.current.value = cleanedSearchValue;

        setSearchTerm(cleanedSearchValue);

        if (searchOrigin !== SEARCH_ORIGIN_HISTORY) {
            addToSearchHistory(cleanedSearchValue);
        }
        window.location.href = `${searchUrl}?${SEARCH_QUERY_URL_PARAM}=${encodeURIComponent(cleanedSearchValue)}&searchmode=m&search_origin=${searchOrigin}`;
    };

    const textColor = `text-clr-${headerLightMode ? 'on-primary-high' : 'primary'}`;

    return (
        <div
            ref={searchContainerRef}
            className={`search-legacy__container${isOpen ? ' -open' : ''}${headerLightMode ? ' -light' : ''}`}
            onBlur={handleBlur}
        >
            <div className="search-legacy__input-field-wrapper">
                <input
                    id={'search'}
                    ref={inputRef}
                    type="text"
                    inputMode="search"
                    enterKeyHint="search"
                    className={`search-legacy__input-field ${textColor} ${keyboardFocus.isKeyboardFocus ? KEYBOARD_FOCUS_RING_CLASSES : ''} rounded`}
                    placeholder=" "
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    // capture props, so that the ring is tracked without touching the focus/blur behaviour above
                    onFocusCapture={keyboardFocus.onFocusCapture}
                    onBlurCapture={keyboardFocus.onBlurCapture}
                    autoComplete="off"
                    value={searchTerm}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => handleSearchChange(e.target.value)}
                />
                <label htmlFor={'search'} className={`search-legacy__input-field-label ${textColor}`}>
                    {searchTextsData?.searchPlaceholder}
                </label>
            </div>
            <button className="search-legacy__search-button" onClick={handleButtonClick} aria-label="Suche abschicken">
                <Icon variant="search" className={`${headerLightMode ? 'fill-clr-on-primary-high' : ''}`} />
            </button>
            <button className="search-legacy__close-button" onClick={() => setIsOpen(false)} aria-label="Suche schließen">
                <Icon variant="closer" className={`${headerLightMode ? 'fill-clr-on-primary-high' : ''}`} />
            </button>

            {isDropdownExpanded && currentList.length > 0 && (
                <SearchDropdownLegacy
                    listMode={listMode}
                    currentList={currentList}
                    deferredSearchTerm={deferredSearchTerm}
                    selectedIndex={selectedIndex}
                    selectionMode={selectionMode}
                    handleSelectItem={handleSelectItem}
                    handleSearchResultItemHover={handleSearchResultItemHover}
                    unselectField={unselectField}
                    clearSearchHistory={clearSearchHistory}
                />
            )}
        </div>
    );
};

export default SearchFieldLegacy;
